<template>
  <div class="line-chart-container">
    <div class="chart-header">
      <h3 class="chart-title">{{ title }}</h3>
      <div class="chart-period">{{ isSimpleArray ? '7 ngày qua' : data.length + ' ngày qua' }}</div>
    </div>
    
    <div class="chart-wrapper">
      <apexchart 
        v-if="chartOptions && chartSeries.length > 0"
        type="line" 
        height="400"
        :options="chartOptions" 
        :series="chartSeries"
      />
      <div v-else class="no-data-message">
        <v-icon size="64" color="grey">mdi-chart-line</v-icon>
        <p class="text-grey mt-2">Không có dữ liệu để hiển thị biểu đồ</p>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'

export default {
  name: 'LineChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    title: {
      type: String,
      default: 'Biểu đồ'
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isSimpleArray() {
      return this.data && this.data.length > 0 && typeof this.data[0] === 'number'
    },
    chartSeries() {
      if (!this.data || this.data.length === 0) return []
      
      // Handle simple array format (for Dashboard)
      if (this.isSimpleArray) {
        return [{
          name: 'Doanh thu',
          data: this.data.map((value, index) => ({
            x: `Ngày ${index + 1}`,
            y: Number(value) || 0
          }))
        }]
      }
      
      // Handle object array format (for Analysis)
      const series = []
      const firstItem = this.data[0]
      
      // Get all numeric keys (excluding 'date')
      const seriesKeys = Object.keys(firstItem).filter(key => 
        key !== 'date' && typeof firstItem[key] === 'number'
      )
      
      seriesKeys.forEach(key => {
        series.push({
          name: key,
          data: this.data.map(item => ({
            x: item.date,
            y: Number(item[key]) || 0
          }))
        })
      })
      
      return series
    },
    chartOptions() {
      return {
        chart: {
          type: 'line',
          height: 400,
          zoom: {
            enabled: false
          },
          toolbar: {
            show: false
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#4CAF50', '#FF9800', '#2196F3', '#9C27B0', '#FF5722'],
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 4
        },
        markers: {
          size: 6,
          strokeWidth: 2,
          strokeColors: '#fff',
          hover: {
            size: 8
          }
        },
        xaxis: {
          type: 'category',
          labels: {
            style: {
              colors: '#666',
              fontSize: '12px'
            }
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#666',
              fontSize: '12px'
            },
            formatter: (value) => {
              return this.formatAxisValue(value)
            }
          }
        },
        tooltip: {
          theme: 'light',
          x: {
            show: true,
            format: 'dd/MM'
          },
          y: {
            formatter: (value) => {
              return this.formatCurrency(value)
            }
          },
          marker: {
            show: true
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          offsetY: -10,
          markers: {
            width: 8,
            height: 8,
            radius: 4
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: {
              height: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      }
    }
  },
  methods: {
    formatCurrency(amount) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    formatAxisValue(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M'
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K'
      } else {
        return Math.round(value).toString()
      }
    }
  }
}
</script>

<style scoped>
.line-chart-container {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.chart-period {
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
}

.chart-wrapper {
  position: relative;
  width: 100%;
  min-height: 400px;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #666;
}

/* Override ApexCharts styles */
:deep(.apexcharts-canvas) {
  margin: 0 auto;
}

:deep(.apexcharts-legend) {
  justify-content: flex-start !important;
}

:deep(.apexcharts-legend-series) {
  margin-right: 20px !important;
}

@media (max-width: 768px) {
  .line-chart-container {
    padding: 16px;
  }
  
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .chart-title {
    font-size: 16px;
  }
  
  .chart-wrapper {
    min-height: 300px;
  }
}

@media (max-width: 480px) {
  .line-chart-container {
    padding: 12px;
  }
  
  .chart-wrapper {
    min-height: 250px;
  }
}
</style> 